import React, { createContext, useContext, useState, useCallback } from "react";
import { Box, IconButton, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Portal from "@mui/material/Portal";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const ToastContext = createContext(null);
export const useToast = () => useContext(ToastContext);

const SEVERITY_CONFIG = {
  success: {
    Icon: CheckCircleRoundedIcon,
    iconBg: "#22c55e",
    titleColor: "#15803d",
    title: "Éxito",
  },
  error: {
    Icon: ErrorRoundedIcon,
    iconBg: "#ef4444",
    titleColor: "#b91c1c",
    title: "Error",
  },
  warning: {
    Icon: WarningRoundedIcon,
    iconBg: "#f59e0b",
    titleColor: "#b45309",
    title: "Advertencia",
  },
  info: {
    Icon: InfoRoundedIcon,
    iconBg: "#3b82f6",
    titleColor: "#1d4ed8",
    title: "Información",
  },
};

function ToastItem({ toast, onClose }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { severity = "info", message } = toast;
  const cfg = SEVERITY_CONFIG[severity] ?? SEVERITY_CONFIG.info;
  const { Icon } = cfg;

  return (
    <Box
      role="alert"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: "14px 12px 14px 16px",
        background: isDark ? "#1e293b" : "#ffffff",
        borderRadius: "18px",
        boxShadow: isDark
          ? "0 8px 32px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)"
          : "0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)",
        border: isDark
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid rgba(0,0,0,0.04)",
        minWidth: 300,
        maxWidth: 420,
        animation: "toastSlideIn 0.22s cubic-bezier(0.34,1.26,0.64,1)",
        "@keyframes toastSlideIn": {
          from: { opacity: 0, transform: "translateY(16px) scale(0.95)" },
          to: { opacity: 1, transform: "translateX(0) scale(1)" },
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          bgcolor: cfg.iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color: "#fff", fontSize: 24 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: cfg.titleColor,
            fontSize: 14,
            lineHeight: 1.3,
          }}
        >
          {cfg.title}
        </Typography>
        <Typography
          sx={{
            color: isDark ? "#94a3b8" : "#64748b",
            fontSize: 13,
            mt: 0.3,
            lineHeight: 1.45,
            wordBreak: "break-word",
          }}
        >
          {message}
        </Typography>
      </Box>

      <IconButton
        size="small"
        onClick={() => onClose(toast.id)}
        aria-label="Cerrar"
        sx={{
          color: isDark ? "#64748b" : "#94a3b8",
          flexShrink: 0,
          alignSelf: "flex-start",
          mt: 0.25,
          "&:hover": { color: isDark ? "#cbd5e1" : "#475569" },
        }}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, severity = "info") => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message: String(message), severity }]);
  }, []);

  const closeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const closeAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Portal>
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column-reverse",
            gap: 1.5,
            pointerEvents: "none",
            "& > *": { pointerEvents: "auto" },
          }}
        >
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onClose={closeToast} />
          ))}
          {toasts.length > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                onClick={closeAll}
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "999px",
                  px: 1.6,
                  py: 0.4,
                  color: "#64748b",
                  bgcolor: "rgba(255,255,255,0.85)",
                  border: "1px solid rgba(0,0,0,0.10)",
                  backdropFilter: "blur(8px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  "&:hover": { bgcolor: "rgba(255,255,255,1)", color: "#334155" },
                }}
              >
                Cerrar todos
              </Button>
            </Box>
          )}
        </Box>
      </Portal>
    </ToastContext.Provider>
  );
}
